import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Building, Send, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicResourceSubmitPage() {
  const [form, setForm] = useState({ organization_name: '', resource_type: '', description: '', contact_info: '', address: '', website: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.organization_name || !form.resource_type) return toast.error('Organization name and type required');
    setLoading(true);
    try {
      await axios.post(`${API}/api/public/resource-submission`, form);
      setSubmitted(true);
    } catch { toast.error('Failed to submit'); } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4" data-testid="resource-submit-success">
        <Card className="max-w-md w-full rounded-2xl border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>Resource Submitted</h2>
            <p className="text-muted-foreground">Thank you! An administrator will review your submission and add it to our directory.</p>
            <Button onClick={() => { setSubmitted(false); setForm({ organization_name: '', resource_type: '', description: '', contact_info: '', address: '', website: '' }); }} variant="outline" className="mt-6 rounded-full">Submit Another</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4" data-testid="public-resource-submit-page">
      <Card className="max-w-md w-full rounded-2xl border-0 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center mx-auto mb-3"><Building className="w-6 h-6 text-white" /></div>
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Share a Community Resource</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Help our community by sharing resources you know about.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5"><Label className="text-sm">Organization Name *</Label><Input value={form.organization_name} onChange={e => setForm({...form, organization_name: e.target.value})} placeholder="Organization name" data-testid="res-org-name" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Resource Type *</Label>
            <Select value={form.resource_type} onValueChange={v => setForm({...form, resource_type: v})}>
              <SelectTrigger data-testid="res-type"><SelectValue placeholder="What type of resource?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="food_pantry">Food Pantry</SelectItem>
                <SelectItem value="clothing">Clothing Closet</SelectItem>
                <SelectItem value="transportation">Transportation</SelectItem>
                <SelectItem value="furniture">Furniture Program</SelectItem>
                <SelectItem value="housing">Housing Assistance</SelectItem>
                <SelectItem value="utility">Utility Assistance</SelectItem>
                <SelectItem value="employment">Employment Help</SelectItem>
                <SelectItem value="healthcare">Healthcare</SelectItem>
                <SelectItem value="legal">Legal Services</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-sm">Description *</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} placeholder="What services do they provide?" rows={2} data-testid="res-description" showCleanup={false} /></div>
          <div className="space-y-1.5"><Label className="text-sm">Contact Info</Label><Input value={form.contact_info} onChange={e => setForm({...form, contact_info: e.target.value})} placeholder="Phone or email" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Street address" /></div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2" data-testid="submit-resource">
            {loading ? 'Submitting...' : <><Send size={14} /> Submit Resource</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
